import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import SponsorsPage from './SponsorsPage';
import { useWindowWidth } from '../hooks/useWindowWidth';

export default function AdminPage() {
  const { user } = useAuth();
  const width = useWindowWidth();
  const isMobile = width < 768;
  const [view, setView] = useState('requests'); // 'requests' | 'users' | 'sponsors'
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [rejectConfirm, setRejectConfirm] = useState(null); // requestId or null

  useEffect(() => {
    fetchData();
  }, [view]);

  const fetchData = async () => {
    setLoading(true);
    setMessage('');
    setError('');
    try {
      if (view === 'requests') {
        const res = await axios.get('/api/admin/requests');
        setRequests(res.data);
      } else if (view === 'users') {
        const res = await axios.get('/api/admin/users');
        setUsers(res.data);
      }
    } catch (err) {
      setError('Fout bij het laden van gegevens.');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      const res = await axios.post(`/api/admin/approve/${requestId}`);
      setMessage(res.data.message);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Fout bij goedkeuren.');
    }
  };

  const handleReject = async () => {
    try {
      await axios.post(`/api/admin/reject/${rejectConfirm}`);
      setRejectConfirm(null);
      setMessage('Aanvraag afgewezen.');
      fetchData();
    } catch (err) {
      setRejectConfirm(null);
      setError('Fout bij afwijzen.');
    }
  };

  const handleUpdateUser = async (userId, field, value) => {
    try {
      await axios.post('/api/admin/update-user', { userId, [field]: value });
      setMessage('Gebruiker bijgewerkt.');
      fetchData();
    } catch (err) {
      setError('Fout bij bijwerken.');
    }
  };

  const roleLabel = (role) => {
    if (role === 'admin') return '👑 Admin';
    if (role === 'redacteur') return '✏ Redacteur';
    if (role === 'commissie') return '✉ Commissie lid';
    if (role === 'readonly') return '👁 Readonly';
    return role;
  };

  const statusLabel = (status) => {
    if (status === 'active') return { label: 'Actief', color: '#2d6a3f', bg: '#f0fff4' };
    if (status === 'inactive') return { label: 'Inactief', color: '#888', bg: '#f5f5f5' };
    return { label: status, color: '#888', bg: '#f5f5f5' };
  };

  return (
    <div style={{ ...styles.wrap, padding: isMobile ? '24px 16px' : '36px 40px' }}>
      <div style={styles.pageTitle}>Beheer</div>

      {/* Tabs */}
      <div style={{ ...styles.tabs, flexWrap: 'wrap' }}>
        <button
          style={{ ...styles.tab, ...(view === 'requests' ? styles.tabActive : {}) }}
          onClick={() => setView('requests')}
        >
          Toegangsaanvragen
          {requests.length > 0 && view !== 'requests' && (
            <span style={styles.badge}>{requests.length}</span>
          )}
        </button>
        <button
          style={{ ...styles.tab, ...(view === 'users' ? styles.tabActive : {}) }}
          onClick={() => setView('users')}
        >
          Gebruikers
        </button>
        <button
          style={{ ...styles.tab, ...(view === 'sponsors' ? styles.tabActive : {}) }}
          onClick={() => setView('sponsors')}
        >
          Sponsoren
        </button>
      </div>

      {message && <div style={styles.successMsg}>{message}</div>}
      {error && <div style={styles.errorMsg}>{error}</div>}

      {loading && view !== 'sponsors' ? (
        <div style={styles.loading}>Laden...</div>
      ) : (
        <>
          {/* Access requests */}
          {view === 'requests' && (
            <div>
              {requests.length === 0 ? (
                <div style={styles.empty}>Geen openstaande aanvragen.</div>
              ) : (
                requests.map(req => (
                  <div key={req.id} style={styles.card}>
                    <div style={styles.cardInfo}>
                      <div style={styles.cardName}>{req.name}</div>
                      <div style={styles.cardEmail}>{req.email}</div>
                      <div style={styles.cardDate}>
                        Aangevraagd op {new Date(req.createdAt).toLocaleDateString('nl-NL')}
                      </div>
                    </div>
                    <div style={styles.cardActions}>
                      <button
                        style={styles.approveBtn}
                        onClick={() => handleApprove(req.id)}
                      >
                        ✓ Goedkeuren
                      </button>
                      <button
                        style={styles.rejectBtn}
                        onClick={() => setRejectConfirm(req.id)}
                      >
                        ✕ Afwijzen
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Users list */}
          {view === 'users' && (
            <div>
              {users.length === 0 ? (
                <div style={styles.empty}>Geen gebruikers gevonden.</div>
              ) : (
                users.map(u => {
                  const s = statusLabel(u.status);
                  return (
                    <div key={u.id} style={styles.card}>
                      <div style={styles.cardInfo}>
                        <div style={styles.cardName}>
                          {u.name}
                          {u.id === user.id && <span style={styles.youBadge}>jij</span>}
                        </div>
                        <div style={styles.cardEmail}>{u.email}</div>
                        <div style={styles.cardMeta}>
                          <span style={{ ...styles.statusBadge, color: s.color, background: s.bg }}>
                            {s.label}
                          </span>
                          <span style={styles.roleBadge}>{roleLabel(u.role)}</span>
                          {u.lastLogin && (
                            <span style={styles.lastLogin}>
                              Laatste login: {new Date(u.lastLogin).toLocaleDateString('nl-NL')}
                            </span>
                          )}
                        </div>
                      </div>
                      {u.id !== user.id && (
                        <div style={styles.cardActions}>
                          <select
                            style={styles.select}
                            value={u.role}
                            onChange={e => handleUpdateUser(u.id, 'role', e.target.value)}
                          >
                            <option value="commissie">Commissie lid</option>
                            <option value="redacteur">Redacteur</option>
                            <option value="admin">Admin</option>
                            <option value="readonly">Readonly</option>
                          </select>
                          {u.status === 'active' ? (
                            <button
                              style={styles.rejectBtn}
                              onClick={() => handleUpdateUser(u.id, 'status', 'inactive')}
                            >
                              Deactiveren
                            </button>
                          ) : (
                            <button
                              style={styles.approveBtn}
                              onClick={() => handleUpdateUser(u.id, 'status', 'active')}
                            >
                              Activeren
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {view === 'sponsors' && <SponsorsPage />}
        </>
      )}

      {/* Reject confirmation modal */}
      {rejectConfirm && (
        <div style={modal.overlay}>
          <div style={modal.box}>
            <div style={modal.title}>Aanvraag afwijzen</div>
            <p style={modal.text}>Weet je zeker dat je deze aanvraag wilt afwijzen? Het wachtwoord wordt verwijderd uit de aanvraag.</p>
            <div style={modal.actions}>
              <button style={modal.cancelBtn} onClick={() => setRejectConfirm(null)}>
                Annuleren
              </button>
              <button style={modal.confirmBtn} onClick={handleReject}>
                Afwijzen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  wrap: {
    padding: '36px 40px',
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },
  pageTitle: {
    fontFamily: 'Georgia, serif',
    fontSize: 26,
    fontWeight: 600,
    color: '#1a2a1e',
    marginBottom: 24,
  },
  tabs: { display: 'flex', gap: 4, marginBottom: 24 },
  tab: {
    padding: '10px 20px', background: '#fff', border: '1px solid #e0dcd6',
    borderRadius: 8, fontSize: 13, color: '#888', cursor: 'pointer',
    fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 8,
  },
  tabActive: { background: '#1a2a1e', color: '#fff', borderColor: '#1a2a1e', fontWeight: 600 },
  badge: {
    background: '#e05555', color: '#fff', borderRadius: 10,
    padding: '2px 7px', fontSize: 11, fontWeight: 700,
  },
  successMsg: {
    background: '#f0fff4', border: '1px solid #b8e6c4', borderRadius: 8,
    padding: '12px 16px', fontSize: 14, color: '#2d6a3f', marginBottom: 16,
  },
  errorMsg: {
    background: '#fff0f0', border: '1px solid #ffcccc', borderRadius: 8,
    padding: '12px 16px', fontSize: 14, color: '#cc0000', marginBottom: 16,
  },
  loading: { textAlign: 'center', color: '#aaa', padding: '40px' },
  empty: { textAlign: 'center', color: '#aaa', padding: '40px', background: '#fff', borderRadius: 12 },
  card: {
    background: '#fff', borderRadius: 12, padding: '20px 24px', marginBottom: 12,
    border: '1px solid #ede9e3', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
  },
  cardInfo: { flex: 1 },
  cardName: { fontSize: 15, fontWeight: 600, color: '#1a2a1e', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 },
  cardEmail: { fontSize: 13, color: '#888', marginBottom: 4 },
  cardDate: { fontSize: 12, color: '#bbb' },
  cardMeta: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 4 },
  cardActions: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  select: {
    padding: '8px 12px', border: '1px solid #e0dcd6', borderRadius: 6,
    fontSize: 13, color: '#444', background: '#faf8f5', fontFamily: 'inherit',
  },
  approveBtn: {
    background: '#3a7a4a', color: '#fff', border: 'none', borderRadius: 6,
    padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  },
  rejectBtn: {
    background: '#fff', color: '#cc0000', border: '1px solid #ffcccc', borderRadius: 6,
    padding: '8px 16px', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
  },
  statusBadge: {
    fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 4,
  },
  roleBadge: { fontSize: 12, color: '#888' },
  lastLogin: { fontSize: 11, color: '#bbb' },
  youBadge: {
    fontSize: 10, background: '#e8f4ec', color: '#3a7a4a', padding: '2px 7px',
    borderRadius: 4, fontWeight: 600, letterSpacing: '0.5px',
  },
};

const modal = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  box: {
    background: '#fff', borderRadius: 16, padding: '32px 36px',
    maxWidth: 420, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  },
  title: {
    fontFamily: 'Georgia, serif', fontSize: 20, fontWeight: 600,
    color: '#1a2a1e', marginBottom: 12,
  },
  text: { fontSize: 14, color: '#555', lineHeight: 1.6, marginBottom: 24 },
  actions: { display: 'flex', gap: 10, justifyContent: 'flex-end' },
  cancelBtn: {
    background: 'transparent', border: '1px solid #e0dcd6', borderRadius: 8,
    padding: '10px 20px', fontSize: 14, color: '#666', cursor: 'pointer',
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },
  confirmBtn: {
    background: '#cc0000', color: '#fff', border: 'none', borderRadius: 8,
    padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },
};
